import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { fetchLatestPacket } from '../../services/api';
import { Packet, NodePosition } from '../../types/api';
import { FiMessageSquare, FiInfo, FiMapPin, FiActivity, FiHelpCircle, FiShare2, FiWifi } from 'react-icons/fi';

interface LatestPacketInfoProps {
  onNewPacket?: (packet: Packet) => void;
  showOfflineNodes: boolean;
  onToggleShowOfflineNodes: () => void;
  capitalizeType?: boolean;
  nodePositions?: NodePosition[];
  packets?: Packet[];
}

const LatestPacketInfo: React.FC<LatestPacketInfoProps> = ({ 
  onNewPacket, 
  showOfflineNodes, 
  onToggleShowOfflineNodes, 
  capitalizeType, 
  nodePositions,
  packets = []
}) => {
  const [latestPacket, setLatestPacket] = useState<Packet | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [consecutiveErrors, setConsecutiveErrors] = useState<number>(0);
  const [backoffTime, setBackoffTime] = useState<number>(2500); // 2.5 seconds for more frequent updates

  // Format elapsed time in seconds with abbreviated "s" format
  const formatElapsedTime = (timestamp: string) => {
    const now = new Date();
    const packetTime = new Date(timestamp);
    const diffSeconds = Math.floor((now.getTime() - packetTime.getTime()) / 1000);
    
    return `${diffSeconds}s`;
  };

  // Get appropriate icon for packet type
  const getPacketTypeIcon = (packetType: string) => {
    switch (packetType.toLowerCase()) {
      case 'text':
        return <FiMessageSquare size={12} />;
      case 'telemetry':
        return <FiActivity size={12} />;
      case 'position':
        return <FiMapPin size={12} />;
      case 'nodeinfo':
        return <FiInfo size={12} />;
      case 'routing':
        return <FiShare2 size={12} />;
      case 'data':
        return <FiActivity size={12} />;
      case 'ping':
      case 'pong':
        return <FiWifi size={12} />;
      default:
        return <FiHelpCircle size={12} />;
    }
  };

  // Get short name for a node
  const getNodeShortName = (nodeId: number | string | null | undefined, fallbackName: string) => {
    if (nodeId == null) return 'Broadcast';
    
    const nodeIdNum = typeof nodeId === 'string' ? parseInt(nodeId, 10) : nodeId;
    
    if (nodePositions) {
      const node = nodePositions.find(n => Number(n.nodeId) === nodeIdNum);
      if (node?.details?.shortName) {
        return node.details.shortName;
      }
    }
    
    return fallbackName;
  };

  // Use useCallback to memoize the fetch function
  const fetchLatestPacketData = useCallback(async () => {
    // Don't fetch if we've fetched within the last 1 second to avoid overwhelming the API
    const now = Date.now();
    if (now - lastFetchTime < 1000) {
      return;
    }
    
    setLastFetchTime(now);
    
    try {
      // Only show loading indicator on initial load
      if (!latestPacket) {
        setLoading(true);
      }
      
      // Use the optimized fetchLatestPacket function
      const packet = await fetchLatestPacket();
      
      if (packet) {
        // Only update and trigger callback if we have a new packet
        if (!latestPacket || packet.id !== latestPacket.id) {
          console.log('New packet detected:', packet.id);
          setLatestPacket(packet);
          if (onNewPacket) {
            onNewPacket(packet);
          }
        }
        
        // Reset error state and backoff on success
        setError(null);
        setConsecutiveErrors(0);
        setBackoffTime(2500); // Reset to 2.5 seconds interval
      }
    } catch (err) {
      console.error('Error fetching latest packet:', err);
      
      // Increment consecutive errors counter
      setConsecutiveErrors(prev => prev + 1);
      
      // Only show error after multiple consecutive failures
      if (consecutiveErrors > 3) {
        setError('Failed to fetch latest packet');
        
        // Implement exponential backoff for repeated failures
        // Cap at 30 seconds to ensure we still try occasionally
        if (consecutiveErrors > 5) {
          const newBackoff = Math.min(backoffTime * 1.5, 30000);
          setBackoffTime(newBackoff);
          console.log(`Backing off packet fetching to ${newBackoff}ms due to repeated failures`);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [onNewPacket, latestPacket, lastFetchTime, consecutiveErrors, backoffTime, nodePositions]);

  useEffect(() => {
    let isMounted = true;
    let intervalId: NodeJS.Timeout;

    // Fetch immediately on mount
    fetchLatestPacketData();

    // Set up interval with dynamic backoff time
    intervalId = setInterval(fetchLatestPacketData, backoffTime);

    // Clean up on unmount
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [fetchLatestPacketData, backoffTime]);

  // Add a manual retry button when in error state
  const handleRetry = () => {
    setError(null);
    setConsecutiveErrors(0);
    setBackoffTime(2500);
    fetchLatestPacketData();
  };

  if (loading && !latestPacket && packets.length === 0) {
    return <Container>Loading latest packet info...</Container>;
  }

  if (error && !latestPacket && packets.length === 0) {
    return (
      <ErrorContainer>
        <div>Error: {error}</div>
        <RetryButton onClick={handleRetry}>Retry</RetryButton>
      </ErrorContainer>
    );
  }

  if (!latestPacket && packets.length === 0) {
    return <Container>No packets available</Container>;
  }
  
  // Use the provided packets if available, otherwise fall back to the latest packet
  const displayPacket = packets.length > 0 ? packets[0] : latestPacket;
  if (!displayPacket) return <Container>No packets available</Container>;

  // --- Look up ShortName --- 
  let sourceShortName = displayPacket.fromNodeName; // Fallback to the name from packet
  let sourceLongName = displayPacket.fromNodeName; // Default to same fallback
  
  if (nodePositions && displayPacket.fromNode != null) {
    // Convert to numbers to ensure consistent type comparison
    const fromNodeAsNumber = Number(displayPacket.fromNode);
    
    const sourceNode = nodePositions.find(node => Number(node.nodeId) === fromNodeAsNumber);
    
    if (sourceNode?.details) {
      if (sourceNode.details.shortName) {
        sourceShortName = sourceNode.details.shortName;
      }
      if (sourceNode.details.longName) {
        sourceLongName = sourceNode.details.longName;
      }
    }
  }
  // --- End ShortName Lookup --- 

  // Compute displayed type with capitalization if requested
  const displayedType = capitalizeType ? displayPacket.type.charAt(0).toUpperCase() + displayPacket.type.slice(1).toLowerCase() : displayPacket.type;

  // Format timestamp
  const timestamp = new Date(displayPacket.timestamp).toLocaleTimeString();

  return (
    <Container>
      <Title>Latest Packet: {sourceShortName}</Title>
      <Content>
        <PacketInfo>
          <strong>From:</strong> {sourceLongName}
        </PacketInfo>
        <PacketInfo>
          <strong>To:</strong> {displayPacket.toNodeName}
        </PacketInfo>
        <PacketInfo>
          <strong>Type:</strong> {displayedType}
        </PacketInfo>
        <PacketInfo>
          <strong>Time:</strong> {timestamp}
        </PacketInfo>
        {displayPacket.snr != null && (
          <PacketInfo>
            <strong>SNR:</strong> {displayPacket.snr.toFixed(1)}
          </PacketInfo>
        )}
        <PacketInfo>
          <label>
            <input type="checkbox" checked={showOfflineNodes} onChange={onToggleShowOfflineNodes} />
             &nbsp;Show Offline Nodes
          </label>
        </PacketInfo>
      </Content>
      
      {/* Last 5 packets summary with modern pill UI */}
      {packets && packets.length > 0 && (
        <RecentPacketsContainer>
          {packets.slice(0, 5).map((packet, idx) => (
            <PacketPill key={packet.id}>
              <NodeName>
                {getNodeShortName(packet.fromNode, packet.fromNodeName)}
              </NodeName>
              <PacketDetails>
                <PacketTypeIcon>
                  {getPacketTypeIcon(packet.type)}
                </PacketTypeIcon>
                <PacketTime>{formatElapsedTime(packet.timestamp)}</PacketTime>
              </PacketDetails>
            </PacketPill>
          ))}
        </RecentPacketsContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
  max-width: 300px;
`;

const Title = styled.h4`
  margin: 0 0 5px 0;
  font-size: 14px;
  color: #2c3e50;
`;

const Content = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const PacketInfo = styled.div`
  font-size: 12px;
  color: #34495e;
  margin-right: 10px;
  
  strong {
    color: #2980b9;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 10px;
  max-width: 300px;
`;

const RetryButton = styled.button`
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #2980b9;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #236a9e;
  }
`;

// New styled components for modern pill UI
const RecentPacketsContainer = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: space-between;
`;

const PacketPill = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: rgba(52, 152, 219, 0.1);
  border-radius: 16px;
  padding: 4px 10px;
  border: 1px solid rgba(52, 152, 219, 0.2);
  flex: 0 0 calc(50% - 3px);
  max-width: calc(50% - 3px);
  
  /* For smaller screen sizes, stack them */
  @media (max-width: 270px) {
    flex: 0 0 100%;
    max-width: 100%;
  }
`;

const NodeName = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #2c3e50;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 50%;
`;

const PacketDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PacketTypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3498db;
`;

const PacketTime = styled.div`
  font-size: 10px;
  color: #34495e;
  font-weight: 500;
`;

export default LatestPacketInfo; 